import React, { createContext, useContext, useState, useEffect } from 'react';

const CattleContext = createContext();
const API = process.env.REACT_APP_BACKEND_URL;

export const useCattle = () => {
  const context = useContext(CattleContext);
  if (!context) {
    throw new Error('useCattle must be used within a CattleProvider');
  }
  return context;
};

export const CattleProvider = ({ children }) => {
  const [cattle, setCattle] = useState([]);
  const [weightRecords, setWeightRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch all cattle on mount
  useEffect(() => {
    fetchCattle();
  }, []);

  const fetchCattle = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await fetch(`${API}/api/cattle`, { headers });
      if (!response.ok) throw new Error('Error al cargar ganado');
      
      const data = await response.json();
      setCattle(data);
    } catch (error) {
      console.error('Error al cargar ganado:', error);
    } finally {
      setLoading(false);
    }
  };

  const addCattle = async (cattleData) => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const response = await fetch(`${API}/api/cattle`, {
        method: 'POST',
        headers,
        body: JSON.stringify(cattleData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al crear ganado');
      }

      const newCattle = await response.json();
      setCattle(prev => [...prev, newCattle]);
      return newCattle;
    } catch (error) {
      console.error('Error al agregar ganado:', error);
      throw error;
    }
  };

  const updateCattle = async (id, cattleData) => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const response = await fetch(`${API}/api/cattle/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(cattleData)
      });

      if (!response.ok) throw new Error('Error al actualizar ganado');

      const updated = await response.json();
      setCattle(prev => prev.map(c => c.id === id ? updated : c));
      return updated;
    } catch (error) {
      console.error('Error al actualizar ganado:', error);
      throw error;
    }
  };

  const deleteCattle = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const response = await fetch(`${API}/api/cattle/${id}`, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) throw new Error('Error al eliminar ganado');

      setCattle(prev => prev.filter(c => c.id !== id));
    } catch (error) {
      console.error('Error al eliminar ganado:', error);
      throw error;
    }
  };

  // Weight records
  const addWeightRecord = async (weightData) => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const response = await fetch(`${API}/api/cattle/${weightData.cattleId}/weights`, {
        method: 'POST',
        headers,
        body: JSON.stringify(weightData)
      });

      if (!response.ok) throw new Error('Error al agregar peso');

      const newRecord = await response.json();
      setWeightRecords(prev => [...prev, newRecord]);
      return newRecord;
    } catch (error) {
      console.error('Error al agregar peso:', error);
      throw error;
    }
  };

  const getWeightHistory = async (cattleId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const response = await fetch(`${API}/api/cattle/${cattleId}/weights`, { headers });
      if (!response.ok) throw new Error('Error al obtener pesos');

      const weights = await response.json();
      return weights;
    } catch (error) {
      console.error('Error al obtener historial de pesos:', error);
      return [];
    }
  };

  const getCattleById = (id) => {
    return cattle.find(c => c.id === id);
  };

  const getLatestWeight = async (cattleId) => {
    try {
      const weights = await getWeightHistory(cattleId);
      return weights.length > 0 ? weights[weights.length - 1] : null;
    } catch (error) {
      return null;
    }
  };

  // Genealogy functions
  const getOffspring = (cattleId) => {
    return cattle.filter(c => c.motherId === cattleId || c.fatherId === cattleId);
  };

  const getAllDescendants = (cattleId) => {
    const descendants = [];
    const findDescendants = (id) => {
      const children = getOffspring(id);
      children.forEach(child => {
        descendants.push(child);
        findDescendants(child.id);
      });
    };
    findDescendants(cattleId);
    return descendants;
  };

  const getMother = (cattleId) => {
    const animal = cattle.find(c => c.id === cattleId);
    if (!animal || !animal.motherId) return null;
    return cattle.find(c => c.id === animal.motherId);
  };

  const getFather = (cattleId) => {
    const animal = cattle.find(c => c.id === cattleId);
    if (!animal || !animal.fatherId) return null;
    return cattle.find(c => c.id === animal.fatherId);
  };

  const isRelated = (cattleId1, cattleId2) => {
    const animal = cattle.find(c => c.id === cattleId1);
    if (!animal) return false;

    // Direct parent check
    if (animal.motherId === cattleId2 || animal.fatherId === cattleId2) {
      return true;
    }

    // Grandparent check
    const mother = getMother(cattleId1);
    const father = getFather(cattleId1);
    
    if (mother && (mother.motherId === cattleId2 || mother.fatherId === cattleId2)) {
      return true;
    }
    
    if (father && (father.motherId === cattleId2 || father.fatherId === cattleId2)) {
      return true;
    }

    return false;
  };

  const linkMother = async (cattleId, motherId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      const response = await fetch(`${API}/api/cattle/${cattleId}/link-mother`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ motherId })
      });

      if (!response.ok) throw new Error('Error al vincular madre');
      
      // Update local state
      setCattle(prev => prev.map(c => 
        c.id === cattleId ? { ...c, motherId } : c
      ));
      
      return { success: true };
    } catch (error) {
      console.error('Error al vincular madre:', error);
      return { success: false, error };
    }
  };

  const linkFather = async (cattleId, fatherId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      const response = await fetch(`${API}/api/cattle/${cattleId}/link-father`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ fatherId })
      });

      if (!response.ok) throw new Error('Error al vincular padre');
      
      // Update local state
      setCattle(prev => prev.map(c => 
        c.id === cattleId ? { ...c, fatherId } : c
      ));
      
      return { success: true };
    } catch (error) {
      console.error('Error al vincular padre:', error);
      return { success: false, error };
    }
  };

  const value = {
    cattle,
    weightRecords,
    loading,
    addCattle,
    updateCattle,
    deleteCattle,
    addWeightRecord,
    getWeightHistory,
    getCattleById,
    getLatestWeight,
    fetchCattle,
    // Genealogy functions
    getOffspring,
    getAllDescendants,
    getMother,
    getFather,
    isRelated,
    linkMother,
    linkFather
  };

  return (
    <CattleContext.Provider value={value}>
      {children}
    </CattleContext.Provider>
  );
};
